/**
 * List view and pagination color definitions
 */
import {palette, createColor} from './palette.colors';

export const listViewColors = {
  BG: createColor(palette.gray.white, palette.dark.content),
  border: createColor(palette.gray.borderLight, palette.dark.techBorder),
  headerText: createColor(palette.blue.onTint, palette.dark.textLight),
  headerBG: createColor(palette.gray.almostWhite, palette.dark.surface),
  footerTextColor: createColor(palette.blue.onTint, palette.dark.textAlt),
  footerCountText: createColor(palette.gray.textDark, palette.dark.textAlt),
  selectedRowBGColor: createColor(palette.blue.sky, palette.dark.surface),
  configuredHeaderText: createColor(palette.blue.onTint, palette.gray.white),
  configuredListText: createColor(palette.blue.onTint, palette.dark.textAlt),
  buttonText: createColor(palette.blue.primary, palette.blue.primary),
  actionText: createColor(palette.gray.darkest, palette.dark.text),
  actionBorder: createColor(palette.gray.neutral, palette.dark.techBorder),
  lightBorder: createColor(palette.gray.divider, palette.dark.techBorder),
};

export const tileViewColors = {
  border: createColor(palette.gray.neutral, palette.dark.techBorder),
  BG: createColor(palette.gray.white, palette.dark.content),
  partsBorder: createColor(palette.gray.neutral, palette.dark.techBorder),
  partsStatusBorder: createColor(palette.gray.neutral, palette.gray.neutral),
  // Work-order card border — default and hover/selected states
  cardBorder: createColor(palette.blue.selectedItemBG, palette.dark.techBorder),  // #E6F1FF
  cardSelectedBorder: createColor(palette.blue.selectedText, palette.blue.selectedText), // #005FCC
  cardSelectedShadow: createColor('#005FCC59', '#005FCC59'),
  cardSelectedButtonBorder: createColor(palette.blue.selectedBorder, palette.blue.selectedBorder), // #66ABFF
};

export const listPaginationColors = {
  enable: createColor(palette.gray.charcoal, palette.gray.dark),
  disable: createColor(palette.gray.dark, palette.gray.dark),
  border: createColor(palette.gray.neutral, palette.dark.techBorder),
  footerTextColor: createColor(palette.blue.onTint, palette.dark.textAlt),
  footerCountText: createColor(palette.gray.textDark, palette.dark.textAlt),
  // WCAG 1.4.3: active page bg uses secondary blue so white text gives 5.1:1 (was #2B78CA → 3.3:1)
  selectedPagination: createColor(palette.blue.secondary, palette.blue.secondary),
  selectedPaginationText: createColor(palette.gray.white, palette.gray.white),
  pagination: createColor(palette.gray.darker, palette.dark.textAlt),
  // WCAG 1.4.3: PREV/NEXT text was #2B78CA (3.6:1 vs white) → #1A65B8 (5.1:1 vs white)
  buttonText: createColor(palette.blue.onTint, palette.blue.secondary),
  dots: createColor(palette.blue.onTint, palette.dark.textAlt),
};

export const priorityColors = {
  highBG: createColor(
    palette.opacity.highPriority,
    palette.opacity.highPriority,
  ),
  highColor: createColor(palette.semantic.darkRed, palette.semantic.darkRed),
  highBorder: createColor(
    palette.opacity.highPriorityBorder,
    palette.opacity.highPriorityBorder,
  ),
  highBGColorDetail: createColor(
    palette.backgrounds.lightestRed,
    palette.backgrounds.lightestRed,
  ),
  highTextColorDetail: createColor(
    palette.semantic.highPriorityRed,
    palette.semantic.highPriorityRed,
  ),
  highBorderColorDetail: createColor(
    palette.opacity.red33,
    palette.opacity.red33,
  ),
  mediumBG: createColor(
    palette.opacity.mediumPriority,
    palette.opacity.mediumPriority,
  ),
  mediumColor: createColor(
    palette.semantic.mediumOrange,
    palette.semantic.mediumOrange,
  ),
  mediumBorder: createColor(
    palette.opacity.mediumPriorityBorder,
    palette.opacity.mediumPriorityBorder,
  ),
  mediumBGColorDetail: createColor(
    palette.backgrounds.lightYellow,
    palette.backgrounds.lightYellow,
  ),
  mediumTextColorDetail: createColor(
    palette.semantic.lightOrange,
    palette.semantic.lightOrange,
  ),
  mediumBorderColorDetail: createColor(
    palette.borders.yellow,
    palette.borders.yellow,
  ),
  lowBG: createColor(palette.opacity.lowPriority, palette.opacity.lowPriority),
  leastPriorityBGColor: createColor(
    palette.opacity.leastPrioriryBG,
    palette.opacity.leastPrioriryBG,
  ),
  lowColor: createColor(
    palette.semantic.lowPriorityGreen,
    palette.semantic.lowPriorityGreen,
  ),
  lowBorder: createColor(
    palette.opacity.lowPriorityBorder,
    palette.opacity.lowPriorityBorder,
  ),
  lowBGColorDetail: createColor(
    palette.gray.extraLight,
    palette.gray.extraLight,
  ),
  lowTextColorDetail: createColor(palette.gray.darker, palette.gray.darker),
  lowBorderColorDetail: createColor(
    palette.gray.borderLight,
    palette.gray.borderLight,
  ),
  defaultStatusColor: createColor(palette.blue.onTint, palette.gray.darkest),
};

export const statusColors = {
  activeBG: createColor(
    palette.opacity.activeStatus,
    palette.opacity.activeStatus,
  ),
  activeColor: createColor(palette.ui.activeBlue, palette.ui.activeBlue),
  activeColorBorder: createColor(
    palette.opacity.activeStatusBorder,
    palette.opacity.activeStatusBorder,
  ),
  completedBG: createColor(
    palette.opacity.completedStatus,
    palette.opacity.completedStatus,
  ),
  completedColor: createColor(
    palette.gray.darkest,
    palette.gray.darkest,
  ),
  completedColorBorder: createColor(
    palette.opacity.completedStatusBorder,
    palette.opacity.completedStatusBorder,
  ),
  assignedBG: createColor(
    palette.opacity.assignedStatus,
    palette.opacity.assignedStatus,
  ),
  assignedColor: createColor(
    palette.semantic.mediumOrange,
    palette.semantic.mediumOrange,
  ),
  assignedColorBorder: createColor(
    palette.opacity.assignedStatusBorder,
    palette.opacity.assignedStatusBorder,
  ),
  closedColorBG: createColor(palette.gray.neutral, palette.gray.neutral),
  closedColorText: createColor(palette.gray.darkest, palette.gray.darkest),
};

export const partsStatusColors = {
  // Requested — Blue (#ECF4FE / #0C6ED0)
  requestedBG: createColor(palette.backgrounds.requestedBlue, 'transparent'),
  requestedBorder: createColor(palette.semantic.readyBlue, palette.semantic.readyBlue),
  requestedText: createColor(palette.blue.onTint, palette.dark.textAlt),
  // Reserved — Amber (#FFF3DE / #A56A03)
  reservedBG: createColor(palette.opacity.mediumPriority, 'transparent'),
  reservedBorder: createColor(palette.opacity.mediumPriorityBorder, palette.opacity.mediumPriorityBorder),
  reservedText: createColor(palette.blue.onTint, palette.dark.textAlt),
  // On Order — Purple (#F1E6FF / #A65EFF)
  onOrderBG: createColor(palette.backgrounds.lightOnOrder, 'transparent'),
  onOrderBorder: createColor(palette.semantic.onOrderPurple, palette.semantic.onOrderPurple),
  onOrderText: createColor(palette.blue.onTint, palette.dark.textAlt),
  // Issued — Green (#E4F1D5 / #007D0F)
  issuedBG: createColor(palette.backgrounds.successBG, 'transparent'),
  issuedBorder: createColor(palette.semantic.issuedGreen, palette.semantic.issuedGreen),
  issuedText: createColor(palette.blue.onTint, palette.dark.textAlt),
};
